// MonthSelectionView.tsx
// Step 3 in the new flow: Premises → Report Type → [Month] → Report
// Shows 12 month tiles with availability counts for the SPECIFIC selected report type.
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { usePremisesStore } from "../../store/store";
import { DotLoader } from "../../components/loader/Loader";
import axiosInstance from "../../common/axiosInstance";
import {
  MONTH_NAMES,
  getPremiseName,
  getReportTypeConfig,
  safeFetchAll,
  getMonthFromDate,
} from "./constants";

const MonthSelectionView: React.FC = () => {
  const { premiseId, reportType } = useParams<{ premiseId: string; reportType: string }>();
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();

  const [monthCounts, setMonthCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const premiseName = getPremiseName(premiseId ?? "", allPremises);
  const reportConfig = getReportTypeConfig(reportType ?? "");
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  // Year options: 2005 → current year (descending)
  const YEAR_OPTIONS = Array.from(
    { length: currentYear - 2004 },
    (_, i) => currentYear - i
  );

  useEffect(() => {
    if (!allPremises.length) fetchPremises();
  }, []);

  useEffect(() => {
    if (!premiseId || !reportConfig) return;
    setLoading(true);

    if (reportType === "equipment-inspections") {
      axiosInstance.get("/api/inspection-assessments/monitoring/by-premises", {
        params: { premisesId: premiseId, year: selectedYear },
      }).then(r => {
        const data = r.data?.data || r.data;
        const inspections: any[] = data?.inspections || [];
        const counts: Record<number, number> = {};
        for (let m = 1; m <= 12; m++) counts[m] = 0;
        inspections.forEach((insp: any) => {
          const m = getMonthFromDate(insp.inspectionDate || insp.createdAt || "");
          if (m >= 1 && m <= 12) counts[m]++;
        });
        setMonthCounts(counts);
      }).finally(() => setLoading(false));
    } else {
      // Fetch all records for this premise + year, group by month
      safeFetchAll(reportConfig.apiEndpoint, { premisesId: premiseId })
        .then(records => {
          const counts: Record<number, number> = {};
          for (let m = 1; m <= 12; m++) counts[m] = 0;
          records.forEach((r: any) => {
            const dateStr = r.date || r.createdAt || "";
            if (!dateStr) return;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return;
            if (d.getFullYear() !== selectedYear) return;
            const m = d.getMonth() + 1;
            if (m >= 1 && m <= 12) counts[m]++;
          });
          setMonthCounts(counts);
        })
        .finally(() => setLoading(false));
    }
  }, [premiseId, reportType, selectedYear]);

  if (!reportConfig) {
    return (
      <div className="w-full px-6 mt-20 pb-10 flex flex-col items-center justify-center py-24 text-center">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Unknown report type</h3>
        <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}`)} className="mt-4 px-5 py-2.5 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] font-medium">Back</button>
      </div>
    );
  }

  return (
    <div className="w-full px-6 mt-20 pb-10">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
          <li><Link to="/fire-system/monitoring" className="hover:text-[#00A8A5] font-medium transition-colors">Monitoring</Link></li>
          <li className="text-gray-300 select-none">›</li>
          <li><Link to={`/fire-system/monitoring/${premiseId}`} className="hover:text-[#00A8A5] transition-colors truncate max-w-[160px] inline-block">{premiseName}</Link></li>
          <li className="text-gray-300 select-none">›</li>
          <li aria-current="page" className="text-gray-800 font-semibold">{reportConfig.label}</li>
        </ol>
      </nav>

      <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}`)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00A8A5] transition-colors mb-6 group">
        <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
        <span>Back</span>
      </button>

      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Select Month</h1>
          <p className="text-gray-500 text-sm mt-1">
            Viewing <span className="font-semibold text-gray-700">{reportConfig.label}</span> records for{" "}
            <span className="font-semibold text-gray-700">{premiseName}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Year selector */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              className="text-sm font-semibold text-gray-700 bg-transparent focus:outline-none cursor-pointer"
            >
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y}{y === currentYear ? " (Current)" : ""}</option>
              ))}
            </select>
          </div>
          {/* Trends shortcut */}
          <button
            onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${reportType}/trends`)}
            className="flex items-center gap-2 px-4 py-2 border-2 border-[#00A8A5] text-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors text-sm font-semibold"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
            </svg>
            View Trends
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24"><DotLoader /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {MONTH_NAMES.map((monthName, idx) => {
            const month = idx + 1;
            const count = monthCounts[month] ?? 0;
            const isCurrent = month === currentMonth;
            return (
              <div
                key={month}
                data-testid="month-tile"
                onClick={() => {
                  if (reportType === "equipment-inspections") {
                    navigate(`/fire-system/monitoring/${premiseId}/equipment-inspections/${month}?year=${selectedYear}`);
                  } else {
                    navigate(`/fire-system/monitoring/${premiseId}/${reportType}/${month}?year=${selectedYear}`);
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    if (reportType === "equipment-inspections") {
                      navigate(`/fire-system/monitoring/${premiseId}/equipment-inspections/${month}?year=${selectedYear}`);
                    } else {
                      navigate(`/fire-system/monitoring/${premiseId}/${reportType}/${month}?year=${selectedYear}`);
                    }
                  }
                }}
                aria-label={`Select ${monthName}`}
                className={[
                  "relative bg-white rounded-xl p-5 text-left shadow-sm cursor-pointer outline-none",
                  "border-2 transition-all duration-200",
                  "hover:shadow-lg hover:border-[#00A8A5] hover:scale-[1.02]",
                  "focus-visible:ring-2 focus-visible:ring-[#00A8A5] focus-visible:ring-offset-2",
                  isCurrent ? "border-[#00A8A5]" : "border-gray-100",
                ].join(" ")}
              >
                <span data-testid={`month-tile-${month}`} className="sr-only" />
                {isCurrent && selectedYear === currentYear && (
                  <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider text-[#00A8A5] bg-[#00A8A5]/10 px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
                <p className="text-gray-900 font-bold text-base mb-3 pr-14">{monthName}</p>
                {count > 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#00A8A5]/10 text-[#00A8A5]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    {count} record{count !== 1 ? "s" : ""}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" /></svg>
                    No data
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MonthSelectionView;
